import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Slider } from "../components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ChevronLeft, AlertCircle, CheckCircle, Star, Sparkles, Loader2, Sun, Moon, Upload, X, Image as ImageIcon, Package, HelpCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../lib/AuthContext";
import { getProducts, getBatchesByProduct, createReview, checkForBlockedPhrases, uploadReviewPhoto } from "../lib/api";
import { CURRENT_CONSENT_VERSION } from "../lib/api/consent";
import type { ProductWithStats, BatchWithProduct } from "../lib/types";
import { toast } from "sonner";
import {
  hapticNotch,
  hapticStarTap,
  hapticToggleTap,
  hapticStepTransition,
  hapticFireworks,
  hapticGoldenFireworks,
} from "../lib/haptics";
import labelExampleImage from "figma:asset/3630bd26c70cb76b71d7b7fb4171be183944492e.png";
import invoiceExampleImage from "figma:asset/ee642e8652d81c884bd6c969edd305fece026708.png";

interface SubmitReviewProps {
  productId?: string;
  onNavigate?: (page: string, id?: string) => void;
}

export function SubmitReview({ productId: preselectedProductId, onNavigate }: SubmitReviewProps) {
  const { user, profile } = useAuth();
  const [step, setStep] = useState(1);
  const [products, setProducts] = useState<ProductWithStats[]>([]);
  const [batches, setBatches] = useState<BatchWithProduct[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingBatches, setLoadingBatches] = useState(false);
  
  const [productId, setProductId] = useState(preselectedProductId || "");
  const [batchId, setBatchId] = useState("");
  const [title, setTitle] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  // Step 2: Detailed rating fields - ALL START UNSELECTED (0)
  const [scentScore, setScentScore] = useState(0);
  const [drynessScore, setDrynessScore] = useState(0);
  const [tasteScore, setTasteScore] = useState(0);
  const [appearanceScore, setAppearanceScore] = useState(0);
  const [packagingGood, setPackagingGood] = useState<boolean | null>(null); // null = unselected
  const [bestUsed, setBestUsed] = useState<'day' | 'night' | 'both' | null>(null); // null = unselected
  
  // Hover states for star ratings
  const [scentHover, setScentHover] = useState(0);
  const [drynessHover, setDrynessHover] = useState(0);
  const [tasteHover, setTasteHover] = useState(0);
  const [appearanceHover, setAppearanceHover] = useState(0);
  
  // Hover states for packaging and best used
  const [packagingHover, setPackagingHover] = useState<boolean | null>(null);
  const [bestUsedHover, setBestUsedHover] = useState<'day' | 'night' | 'both' | null>(null);
  
  // Golden Review - 1 in 100 chance (1%)
  const [isGoldenReview] = useState(() => {
    const isGolden = Math.random() < 0.01;
    if (isGolden) {
      console.log('🌟✨ GOLDEN REVIEW ACTIVATED! ✨🌟');
    }
    return isGolden;
  });
  
  // Step 4: Overall effectiveness and repurchase
  const [effectiveness, setEffectiveness] = useState(50); // Slider 1-100
  const [valueForMoney, setValueForMoney] = useState(5); // Slider 1-10
  const [repeatPurchase, setRepeatPurchase] = useState(true);

  // Refs to track previous slider values for haptic notch detection
  const prevEffectivenessRef = React.useRef(50);
  const prevValueForMoneyRef = React.useRef(5);
  
  // Pack label photo upload state - TWO PHOTOS REQUIRED (before CB and after CB)
  const [packLabelBeforeFile, setPackLabelBeforeFile] = useState<File | null>(null);
  const [packLabelBeforePreview, setPackLabelBeforePreview] = useState<string | null>(null);
  const [packLabelBeforeUrl, setPackLabelBeforeUrl] = useState<string | null>(null);
  const [uploadingPackLabelBefore, setUploadingPackLabelBefore] = useState(false);
  
  const [packLabelAfterFile, setPackLabelAfterFile] = useState<File | null>(null);
  const [packLabelAfterPreview, setPackLabelAfterPreview] = useState<string | null>(null);
  const [packLabelAfterUrl, setPackLabelAfterUrl] = useState<string | null>(null);
  const [uploadingPackLabelAfter, setUploadingPackLabelAfter] = useState(false);
  
  // Prescription invoice photo upload state (REQUIRED)
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoicePreview, setInvoicePreview] = useState<string | null>(null);
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  
  // Step 3: Multiple photo upload state (up to 2 photos)
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  // Validation: Check if all Step 2 ratings are complete
  const isStep2Complete = () => {
    return scentScore > 0 && 
           drynessScore > 0 && 
           tasteScore > 0 && 
           appearanceScore > 0 && 
           packagingGood !== null && 
           bestUsed !== null;
  };

  // Auto-calculate grade from all ratings
  const calculateGrade = (): string => {
    // Handle unselected ratings
    if (!isStep2Complete()) {
      console.log('Grade calculation: Step 2 incomplete', {
        scentScore,
        drynessScore,
        tasteScore,
        appearanceScore,
        packagingGood,
        bestUsed
      });
      return "—";
    }
    
    // 1. Star Categories (1-5 stars each) - 4 categories = 20 pts max
    const starTotal = scentScore + drynessScore + tasteScore + appearanceScore;
    const starPoints = (starTotal / 20) * 60; // Stars = 60% of final score
    
    // 2. Effectiveness Slider (1-100) scaled to 20 pts
    const effectivenessPoints = (effectiveness / 100) * 20;
    
    // 3. Value for Money (1-10) scaled to 10 pts
    const valueForMoneyPoints = (valueForMoney / 10) * 10;
    
    // 4. Buy Again (0 or 5 pts)
    const buyAgainPoints = repeatPurchase ? 5 : 0;
    
    // 5. Packaging Binary Penalty (0 or -5 pts)
    const packagingPenalty = packagingGood ? 0 : 5;
    
    // Final Score
    const finalScore = starPoints + effectivenessPoints + valueForMoneyPoints + buyAgainPoints - packagingPenalty;
    
    console.log('Grade calculation:', {
      starTotal,
      starPoints,
      effectivenessPoints,
      valueForMoneyPoints,
      buyAgainPoints,
      packagingPenalty,
      finalScore
    });
    
    // Convert to grade bands with +/- variants
    if (finalScore >= 97) return "A+";
    if (finalScore >= 93) return "A";
    if (finalScore >= 90) return "A-";
    if (finalScore >= 87) return "B+";
    if (finalScore >= 83) return "B";
    if (finalScore >= 80) return "B-";
    if (finalScore >= 77) return "C+";
    if (finalScore >= 73) return "C";
    if (finalScore >= 70) return "C-";
    if (finalScore >= 60) return "D";
    return "F";
  };
  
  // Auto-calculate star rating from grade
  const calculateStars = (grade: string): number => {
    if (grade === "A+" || grade === "A" || grade === "A-") return 5;
    if (grade === "B+" || grade === "B" || grade === "B-") return 4;
    if (grade === "C+" || grade === "C" || grade === "C-") return 3;
    if (grade === "D") return 2;
    return 1; // F
  };

  const currentGrade = calculateGrade();
  const currentStars = calculateStars(currentGrade);

  // Load products on mount
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const data = await getProducts({ active: true });
        setProducts(data);
      } catch (error) {
        console.error("Error loading products:", error);
        toast.error("Failed to load products");
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchProducts();
  }, []);

  // Load batches when product is selected
  useEffect(() => {
    const fetchBatches = async () => {
      if (!productId) {
        setBatches([]);
        return;
      }

      try {
        setLoadingBatches(true);
        const data = await getBatchesByProduct(productId);
        setBatches(data);
      } catch (error) {
        console.error("Error loading batches:", error);
        toast.error("Failed to load batches");
      } finally {
        setLoadingBatches(false);
      }
    };

    fetchBatches();
  }, [productId]);

  const selectedProduct = products.find(p => p.id === productId);

  const getGradeColor = (grade: string) => {
    if (grade.startsWith("A")) return "text-primary";
    if (grade.startsWith("B")) return "text-blue-600";
    if (grade.startsWith("C")) return "text-yellow-600";
    return "text-muted-foreground";
  };

  const getGradientClass = (grade: string) => {
    if (grade.startsWith("A")) return "gradient-grade-a";
    if (grade.startsWith("B")) return "gradient-grade-b";
    if (grade.startsWith("C")) return "gradient-grade-c";
    return "bg-muted";
  };

  const progressPercentage = (step / 4) * 100;

  // Pack Label Photo Handlers - BEFORE CB (Original Label)
  const handlePackLabelBeforeSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be smaller than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPackLabelBeforePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    setPackLabelBeforeFile(file);
  };

  const handlePackLabelBeforeRemove = () => {
    setPackLabelBeforeFile(null);
    setPackLabelBeforePreview(null);
    setPackLabelBeforeUrl(null);
  };

  const handlePackLabelBeforeUpload = async () => {
    if (!packLabelBeforeFile) return;

    if (!user) {
      toast.error('Please sign in to upload photos');
      onNavigate && onNavigate('signin');
      return;
    }

    setUploadingPackLabelBefore(true);
    try {
      console.log('📦 Starting pack label (before CB) upload for user:', user.email);
      const result = await uploadReviewPhoto(packLabelBeforeFile);
      setPackLabelBeforeUrl(result.url);
      toast.success('Original pack label uploaded successfully!');
      console.log('✅ Pack label (before) uploaded:', result);
    } catch (error: any) {
      console.error('❌ Pack label upload error:', error);
      
      if (error.message.includes('logged in') || error.message.includes('authentication')) {
        toast.error('Authentication error. Please try logging in again.');
        setTimeout(() => {
          onNavigate && onNavigate('signin');
        }, 2000);
      } else {
        toast.error(error.message || 'Failed to upload pack label');
      }
      
      handlePackLabelBeforeRemove();
    } finally {
      setUploadingPackLabelBefore(false);
    }
  };

  // Pack Label Photo Handlers - AFTER CB (With CB Written)
  const handlePackLabelAfterSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be smaller than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPackLabelAfterPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    setPackLabelAfterFile(file);
  };

  const handlePackLabelAfterRemove = () => {
    setPackLabelAfterFile(null);
    setPackLabelAfterPreview(null);
    setPackLabelAfterUrl(null);
  };

  const handlePackLabelAfterUpload = async () => {
    if (!packLabelAfterFile) return;

    if (!user) {
      toast.error('Please sign in to upload photos');
      onNavigate && onNavigate('signin');
      return;
    }

    setUploadingPackLabelAfter(true);
    try {
      console.log('📦 Starting pack label (with CB) upload for user:', user.email);
      const result = await uploadReviewPhoto(packLabelAfterFile);
      setPackLabelAfterUrl(result.url);
      toast.success('Pack label with "CB" uploaded successfully!');
      console.log('✅ Pack label (after) uploaded:', result);
    } catch (error: any) {
      console.error('❌ Pack label upload error:', error);
      
      if (error.message.includes('logged in') || error.message.includes('authentication')) {
        toast.error('Authentication error. Please try logging in again.');
        setTimeout(() => {
          onNavigate && onNavigate('signin');
        }, 2000);
      } else {
        toast.error(error.message || 'Failed to upload pack label');
      }
      
      handlePackLabelAfterRemove();
    } finally {
      setUploadingPackLabelAfter(false);
    }
  };

  // Invoice Photo Handlers (REQUIRED)
  const handleInvoiceSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be smaller than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setInvoicePreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    setInvoiceFile(file);
  };

  const handleInvoiceRemove = () => {
    setInvoiceFile(null);
    setInvoicePreview(null);
    setInvoiceUrl(null);
  };

  const handleInvoiceUpload = async () => {
    if (!invoiceFile) return;

    if (!user) {
      toast.error('Please sign in to upload photos');
      onNavigate && onNavigate('signin');
      return;
    }

    setUploadingInvoice(true);
    try {
      console.log('📄 Starting prescription invoice upload for user:', user.email);
      const result = await uploadReviewPhoto(invoiceFile);
      setInvoiceUrl(result.url);
      toast.success('Prescription invoice uploaded successfully! Ready for verification.');
      console.log('✅ Invoice uploaded:', result);
    } catch (error: any) {
      console.error('❌ Invoice upload error:', error);
      
      if (error.message.includes('logged in') || error.message.includes('authentication')) {
        toast.error('Authentication error. Please try logging in again.');
        setTimeout(() => {
          onNavigate && onNavigate('signin');
        }, 2000);
      } else {
        toast.error(error.message || 'Failed to upload prescription invoice');
      }
      
      handleInvoiceRemove();
    } finally {
      setUploadingInvoice(false);
    }
  };

  // Multiple Photo Handlers (up to 2 photos)
  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Check if adding these files would exceed 2 photos
    if (photoFiles.length + files.length > 2) {
      toast.error('You can upload up to 2 photos');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please upload JPEG, PNG, or WebP images only');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error('Each photo must be smaller than 5MB');
        return;
      }
    }

    // Create previews
    const newPreviews: string[] = [];
    for (const file of files) {
      const reader = new FileReader();
      await new Promise<void>((resolve) => {
        reader.onloadend = () => {
          newPreviews.push(reader.result as string);
          resolve();
        };
        reader.readAsDataURL(file);
      });
    }

    setPhotoFiles([...photoFiles, ...files]);
    setPhotoPreviews([...photoPreviews, ...newPreviews]);
  };

  const handlePhotoRemove = (index: number) => {
    setPhotoFiles(photoFiles.filter((_, i) => i !== index));
    setPhotoPreviews(photoPreviews.filter((_, i) => i !== index));
    setPhotoUrls(photoUrls.filter((_, i) => i !== index));
  };

  const handlePhotosUpload = async () => {
    if (photoFiles.length === 0) return;

    if (!user) {
      toast.error('Please sign in to upload photos');
      onNavigate && onNavigate('signin');
      return;
    }

    setUploadingPhotos(true);
    try {
      const uploadedUrls: string[] = [];
      
      for (const file of photoFiles) {
        console.log('📸 Uploading photo for user:', user.email);
        const result = await uploadReviewPhoto(file);
        uploadedUrls.push(result.url);
      }
      
      setPhotoUrls(uploadedUrls);
      toast.success(`${uploadedUrls.length} photo(s) uploaded successfully! EXIF data removed for privacy.`);
      console.log('✅ Photos uploaded:', uploadedUrls);
    } catch (error: any) {
      console.error('❌ Photo upload error:', error);
      
      if (error.message.includes('logged in') || error.message.includes('authentication')) {
        toast.error('Authentication error. Please try logging in again.');
        setTimeout(() => {
          onNavigate && onNavigate('signin');
        }, 2000);
      } else {
        toast.error(error.message || 'Failed to upload photos');
      }
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error("Please sign in to submit a review");
      onNavigate && onNavigate("signin");
      return;
    }

    // Product and batch are no longer required - they will be assigned during moderation
    // based on the pack label photos uploaded

    setIsSubmitting(true);

    try {
      // Check for blocked phrases in review content
      const contentToCheck = `${title} ${reviewText}`;
      const { hasBlocked, matches } = await checkForBlockedPhrases(contentToCheck);

      if (hasBlocked) {
        const criticalMatches = matches.filter(m => m.severity === "critical");
        if (criticalMatches.length > 0) {
          toast.error("Your review contains prohibited content and cannot be submitted");
          setIsSubmitting(false);
          return;
        } else {
          toast.warning("Your review contains flagged content and will be reviewed by moderators");
        }
      }

      // Upload photos if not already uploaded
      let finalPhotoUrls = photoUrls;
      if (photoFiles.length > 0 && photoUrls.length === 0) {
        try {
          const uploadedUrls: string[] = [];
          for (const file of photoFiles) {
            const result = await uploadReviewPhoto(file);
            uploadedUrls.push(result.url);
          }
          finalPhotoUrls = uploadedUrls;
          toast.success('Photos uploaded with EXIF data removed');
        } catch (error: any) {
          toast.error(`Photo upload failed: ${error.message}`);
        }
      }

      // Submit review - use first photo for photo_url (for backwards compatibility)
      await createReview({
        product_id: null, // Product will be assigned during moderation based on pack label
        batch_id: null, // Batch will be assigned during moderation if applicable
        author_id: user.id,
        stars: currentStars, // Auto-calculated from grade
        title: title || null,
        body: reviewText || null,
        photo_url: finalPhotoUrls[0] || null,
        pack_label_before_url: packLabelBeforeUrl || null,
        pack_label_after_url: packLabelAfterUrl || null,
        prescription_invoice_url: invoiceUrl || null,
        value_for_money: valueForMoney,
        consent_version: CURRENT_CONSENT_VERSION,
        mod_state: hasBlocked ? "pending" : "approved",
        scent_score: scentScore,
        dryness_score: drynessScore,
        taste_score: tasteScore,
        appearance_score: appearanceScore,
        packaging_score: packagingGood ? 5 : 1,
        scent_rating: scentScore,
        dryness_rating: drynessScore,
        taste_rating: tasteScore,
        appearance_rating: appearanceScore,
        packaging_rating: packagingGood ? 5 : 1, // Convert Good/Bad to 5/1
        best_used: bestUsed || 'both',
        repeat_purchase: repeatPurchase,
        author_verified_at_submission: profile?.is_verified_patient || false,
      });

      setSubmitted(true);
      // 🎆 Fireworks haptic celebration on successful submission!
      if (isGoldenReview) {
        hapticGoldenFireworks();
      } else {
        hapticFireworks();
      }
      toast.success("Review submitted successfully!");
    } catch (error: any) {
      console.error("Error submitting review:", error);
      
      toast.error(error.message || "Failed to submit review");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-3 sm:px-4 lg:px-8 gradient-mesh py-12 sm:py-0">
        <div className="w-full max-w-2xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Card className="p-6 sm:p-10 md:p-12 text-center glass-card">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-full gradient-green flex items-center justify-center mx-auto mb-5 sm:mb-6 shadow-xl"
              >
                <CheckCircle className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
              </motion.div>
              
              <h2 className="text-2xl sm:text-3xl mb-3 sm:mb-4 px-4">Review Submitted!</h2>
              
              <div className="mb-6 sm:mb-8">
                <div className={`${getGradientClass(currentGrade)} w-28 h-28 sm:w-32 sm:h-32 rounded-3xl mx-auto flex items-center justify-center shadow-2xl mb-3 sm:mb-4`}>
                  <span className="text-4xl sm:text-5xl text-white">{currentGrade}</span>
                </div>
                <p className="text-base sm:text-lg text-muted-foreground px-4">
                  You gave this product a grade of <span className={`font-medium ${getGradeColor(currentGrade)}`}>{currentGrade}</span>
                </p>
              </div>

              <p className="text-sm sm:text-base text-muted-foreground mb-2 px-4">
                Thank you for contributing to the CannaBias community! Your review helps other patients make informed decisions.
              </p>
              
              <p className="text-xs sm:text-sm text-amber-600 dark:text-amber-400 mb-6 sm:mb-8 px-4">
                For everyone's safety, reviews can take up to 48 hours for approval.
              </p>

              <div className="space-y-3">
                <Button
                  className="w-full gradient-green hover:opacity-90 transition-opacity h-11 sm:h-10"
                  onClick={() => selectedProduct && onNavigate && onNavigate("product-detail", selectedProduct.id)}
                >
                  View Product
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-11 sm:h-10"
                  onClick={() => onNavigate && onNavigate("discover")}
                >
                  Back to Discover
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 sm:pt-24 pb-12 sm:pb-20 px-3 sm:px-4 lg:px-8 gradient-mesh">
      <div className="max-w-3xl mx-auto">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Button
            variant="ghost"
            className="mb-4 sm:mb-6 hover:bg-primary/10 h-11"
            onClick={() => onNavigate && onNavigate("discover")}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to Discover
          </Button>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-6 sm:mb-8"
        >
          <h1 className="text-3xl sm:text-4xl mb-2 sm:mb-3 px-2">Share Your Experience</h1>
          <p className="text-lg sm:text-xl text-muted-foreground px-4">
            {step === 1 && "Upload your pack label and prescription photos. We'll match them to the correct product during review."}
            {step === 2 && "We're patients ourselves and we want unbiased transparency too."}
            {step === 3 && "We promise to stay independent from any clinic, grower or pharmacy."}
            {step === 4 && "Thank you for supporting the patient first community!"}
          </p>
        </motion.div>

        {/* Progress Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="mb-6 sm:mb-8"
        >
          <div className="flex items-center justify-between mb-2 text-sm">
            <span className="text-muted-foreground">Step {step} of 4</span>
            <span className="text-muted-foreground">{Math.round(progressPercentage)}% Complete</span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </motion.div>

        <form onSubmit={handleSubmit}>
          <AnimatePresence mode="wait">
            {/* Step 1: Upload Pack Label (OPTIONAL with Skip) + Select Product */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.4 }}
              >
                <Card className="p-4 sm:p-6 md:p-8 glass-card">
                  <div className="mb-4 sm:mb-6">
                    <h2 className="text-xl sm:text-2xl">Start the Review Process</h2>
                    <p className="text-sm text-muted-foreground mt-2">
                      When completing your review, please only consider UK medical products. By starting this process you agree you are a registered patient who is prescribed medicinal cannabis.
                    </p>
                  </div>
                  
                  {/* Pack Label Instructions Notice */}
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Package className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                        </div>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-foreground">
                          Upload photos of your pack label adhering to the requirements below. Our moderation team will match your review to the correct product based on the information shown.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4 sm:space-y-6">
                    {/* Pack Label Upload - Photo 1: WITHOUT CB */}
                    <div>
                      <div className="flex items-center gap-2 mb-2 sm:mb-3">
                        <Label className="block text-base sm:text-lg">Pack Label Photo 1: Original Label (WITHOUT "CB")</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                              aria-label="Help - See label example"
                            >
                              <HelpCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent 
                            className="w-[90vw] max-w-[600px] p-3 sm:p-4" 
                            side="bottom"
                            align="start"
                            sideOffset={8}
                          >
                            <div className="space-y-3">
                              <h3 className="font-semibold text-sm sm:text-base">Photo 1: Original Label (Before Writing CB)</h3>
                              <div className="rounded-lg overflow-hidden border border-border">
                                <img 
                                  src={labelExampleImage} 
                                  alt="Example of medical cannabis label showing product name, CBD/THC content, cultivar, batch number, and expiry date"
                                  className="w-full h-auto"
                                />
                              </div>
                              <div className="space-y-2">
                                <p className="text-xs sm:text-sm font-medium text-foreground">
                                  Take a clear photo of your original label WITHOUT "CB" written on it. This must clearly show:
                                </p>
                                <ul className="text-xs sm:text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                                  <li><strong>Product name</strong></li>
                                  <li><strong>Batch number (BN)</strong></li>
                                  <li><strong>Expiry date (EXP)</strong></li>
                                </ul>
                                <p className="text-xs sm:text-sm text-foreground pt-2">
                                  After uploading this, you'll then write "CB" on the label and upload a second photo.
                                </p>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                        
                        {!packLabelBeforePreview ? (
                          <label className="flex flex-col items-center justify-center w-full h-56 sm:h-48 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all active:scale-[0.98]">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mb-3 sm:mb-4">
                                <ImageIcon className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
                              </div>
                              <p className="text-sm sm:text-base mb-1 px-4 text-center">Click to upload original label (without "CB")</p>
                              <p className="text-xs text-muted-foreground">JPEG, PNG, or WebP (max 5MB)</p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/jpeg,image/jpg,image/png,image/webp"
                              onChange={handlePackLabelBeforeSelect}
                            />
                          </label>
                        ) : (
                          <div className="relative">
                            <img
                              src={packLabelBeforePreview}
                              alt="Original Pack Label Preview (Before CB)"
                              className="w-full h-64 object-contain rounded-xl border border-border bg-muted/30"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute top-2 right-2"
                              onClick={handlePackLabelBeforeRemove}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                            {!packLabelBeforeUrl && (
                              <div className="mt-3">
                                <Button
                                  type="button"
                                  onClick={handlePackLabelBeforeUpload}
                                  disabled={uploadingPackLabelBefore}
                                  className="w-full gradient-green hover:opacity-90 transition-opacity h-11 sm:h-12"
                                >
                                  {uploadingPackLabelBefore ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      <span className="text-sm sm:text-base">Uploading & Processing...</span>
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="w-4 h-4 mr-2" />
                                      <span className="text-sm sm:text-base">Upload Original Label</span>
                                    </>
                                  )}
                                </Button>
                              </div>
                            )}
                            {packLabelBeforeUrl && (
                              <div className="mt-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                  <p className="text-sm text-green-700 dark:text-green-300">
                                    Original label uploaded successfully!
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                    
                    {/* Pack Label Upload - Photo 2: WITH CB */}
                    <div>
                      <div className="flex items-center gap-2 mb-2 sm:mb-3">
                        <Label className="block text-base sm:text-lg">Pack Label Photo 2: Same Label WITH "CB" Written</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                              aria-label="Help - See label with CB example"
                            >
                              <HelpCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent 
                            className="w-[90vw] max-w-[600px] p-3 sm:p-4" 
                            side="bottom"
                            align="start"
                            sideOffset={8}
                          >
                            <div className="space-y-3">
                              <h3 className="font-semibold text-sm sm:text-base">Photo 2: After Writing "CB" on Label</h3>
                              <div className="rounded-lg overflow-hidden border border-border">
                                <img 
                                  src={labelExampleImage} 
                                  alt="Example of medical cannabis label with CB written on it"
                                  className="w-full h-auto"
                                />
                              </div>
                              <div className="space-y-2">
                                <p className="text-xs sm:text-sm font-medium text-foreground">
                                  After uploading the original label, write "CB" clearly on the same label and take another photo showing:
                                </p>
                                <ul className="text-xs sm:text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                                  <li><strong>"CB" written clearly on the label</strong></li>
                                  <li><strong>Same product name, batch number, and expiry date</strong> as Photo 1</li>
                                </ul>
                                <p className="text-xs sm:text-sm text-foreground pt-2">
                                  This helps us verify your review is genuine and prevents duplicate reviews.
                                </p>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                        
                        {!packLabelAfterPreview ? (
                          <label className="flex flex-col items-center justify-center w-full h-56 sm:h-48 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all active:scale-[0.98]">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mb-3 sm:mb-4">
                                <ImageIcon className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
                              </div>
                              <p className="text-sm sm:text-base mb-1 px-4 text-center">Click to upload label with "CB" written</p>
                              <p className="text-xs text-muted-foreground">JPEG, PNG, or WebP (max 5MB)</p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/jpeg,image/jpg,image/png,image/webp"
                              onChange={handlePackLabelAfterSelect}
                            />
                          </label>
                        ) : (
                          <div className="relative">
                            <img
                              src={packLabelAfterPreview}
                              alt="Pack Label Preview (With CB)"
                              className="w-full h-64 object-contain rounded-xl border border-border bg-muted/30"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute top-2 right-2"
                              onClick={handlePackLabelAfterRemove}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                            {!packLabelAfterUrl && (
                              <div className="mt-3">
                                <Button
                                  type="button"
                                  onClick={handlePackLabelAfterUpload}
                                  disabled={uploadingPackLabelAfter}
                                  className="w-full gradient-green hover:opacity-90 transition-opacity h-11 sm:h-12"
                                >
                                  {uploadingPackLabelAfter ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      <span className="text-sm sm:text-base">Uploading & Processing...</span>
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="w-4 h-4 mr-2" />
                                      <span className="text-sm sm:text-base">Upload Label with "CB"</span>
                                    </>
                                  )}
                                </Button>
                              </div>
                            )}
                            {packLabelAfterUrl && (
                              <div className="mt-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                  <p className="text-sm text-green-700 dark:text-green-300">
                                    Label with "CB" uploaded successfully!
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                      {/* Combined Requirements Notice */}
                      <div className="mt-3 sm:mt-4 p-3 sm:p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                        <div className="flex items-start gap-2 sm:gap-3">
                          <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="text-xs sm:text-sm mb-2 text-amber-900 dark:text-amber-100">Pack Label Photo Requirements</h4>
                            <ul className="text-xs text-amber-800 dark:text-amber-200 space-y-1">
                              <li>• Upload TWO photos of the same label</li>
                              <li>• Photo 1: Original label WITHOUT "CB" written</li>
                              <li>• Photo 2: Same label WITH "CB" written clearly</li>
                              <li>• Both photos must show product name, batch number, and expiry date</li>
                              <li>• Must NOT contain any personal information</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Prescription Invoice Upload */}
                    <div className="mt-6 sm:mt-8">
                      <div className="flex items-center gap-2 mb-2 sm:mb-3">
                        <Label className="block text-base sm:text-lg">Prescription Invoice Photo</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                              aria-label="Help - See invoice example"
                            >
                              <HelpCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent 
                            className="w-[90vw] max-w-[600px] p-3 sm:p-4" 
                            side="bottom"
                            align="start"
                            sideOffset={8}
                          >
                            <div className="space-y-3">
                              <h3 className="font-semibold text-sm sm:text-base">Required Invoice Photo Details</h3>
                              <div className="rounded-lg overflow-hidden border border-border">
                                <img 
                                  src={invoiceExampleImage} 
                                  alt="Example of prescription invoice showing product name and date"
                                  className="w-full h-auto"
                                />
                              </div>
                              <div className="space-y-2">
                                <p className="text-xs sm:text-sm font-medium text-foreground">
                                  Your invoice must show:
                                </p>
                                <ul className="text-xs sm:text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                                  <li><strong>The same product name</strong> as shown in the pack label above</li>
                                  <li><strong>Invoice date</strong> within the last 30 days</li>
                                  <li><strong>All personal information edited out</strong> (name, address, etc.)</li>
                                </ul>
                                <p className="text-xs sm:text-sm text-foreground pt-2">
                                  This helps us verify you're reviewing a product you've actually purchased recently.
                                </p>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                        
                        {!invoicePreview ? (
                          <label className="flex flex-col items-center justify-center w-full h-56 sm:h-48 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all active:scale-[0.98]">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center mb-3 sm:mb-4">
                                <ImageIcon className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
                              </div>
                              <p className="text-sm sm:text-base mb-1 px-4 text-center">Click to upload prescription invoice</p>
                              <p className="text-xs text-muted-foreground">JPEG, PNG, or WebP (max 5MB)</p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/jpeg,image/jpg,image/png,image/webp"
                              onChange={handleInvoiceSelect}
                            />
                          </label>
                        ) : (
                          <div className="relative">
                            <img
                              src={invoicePreview}
                              alt="Prescription Invoice Preview"
                              className="w-full h-64 object-contain rounded-xl border border-border bg-muted/30"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              className="absolute top-2 right-2"
                              onClick={handleInvoiceRemove}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                            {!invoiceUrl && (
                              <div className="mt-3">
                                <Button
                                  type="button"
                                  onClick={handleInvoiceUpload}
                                  disabled={uploadingInvoice}
                                  className="w-full gradient-green hover:opacity-90 transition-opacity h-11 sm:h-12"
                                >
                                  {uploadingInvoice ? (
                                    <>
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                      <span className="text-sm sm:text-base">Uploading & Processing...</span>
                                    </>
                                  ) : (
                                    <>
                                      <Upload className="w-4 h-4 mr-2" />
                                      <span className="text-sm sm:text-base">Upload Invoice</span>
                                    </>
                                  )}
                                </Button>
                              </div>
                            )}
                            {invoiceUrl && (
                              <div className="mt-3 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                                <div className="flex items-center gap-2">
                                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                                  <p className="text-sm text-green-700 dark:text-green-300">
                                    Prescription invoice uploaded successfully!
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        
                      {/* Invoice Requirements */}
                      <div className="mt-3 sm:mt-4 p-3 sm:p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                        <div className="flex items-start gap-2 sm:gap-3">
                          <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <h4 className="text-xs sm:text-sm mb-2 text-amber-900 dark:text-amber-100">Prescription Invoice Requirements</h4>
                            <ul className="text-xs text-amber-800 dark:text-amber-200 space-y-1">
                              <li>• Must show the same product as the pack label above</li>
                              <li>• Must be dated within the last 30 days</li>
                              <li>• Must NOT contain any personal information</li>
                              <li>• Please edit out your name, address, and any other personal details</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-center mt-6 sm:mt-8">
                    <Button
                      type="button"
                      onClick={() => {
                        hapticStepTransition();
                        setStep(2);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="gradient-green hover:opacity-90 transition-opacity px-6 sm:px-8 h-11 sm:h-10 w-full sm:w-auto"
                    >
                      {packLabelBeforeUrl && packLabelAfterUrl && invoiceUrl ? "Continue" : "Next: Grade Product"}
                    </Button>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Step 2: Grade Your Product */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="p-4 sm:p-6 md:p-8 glass-card">
                  <h2 className="text-xl sm:text-2xl mb-4 sm:mb-6">Grade Your Product</h2>
                  
                  {/* Anonymous Grading Notice */}
                  <div className="mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
                    <div className="flex items-start gap-2 sm:gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
                        </div>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm text-foreground">
                          Whether you're a connoisseur, or it's your first review, please grade fairly and to your own personal preference - reviews are always anonymous.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {isGoldenReview && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="mb-4 sm:mb-6 p-3 sm:p-4 rounded-xl bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/30 dark:to-yellow-950/30 border-2 border-amber-300 dark:border-amber-700"
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="flex-shrink-0">
                          <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-amber-500 animate-pulse" />
                        </div>
                        <div>
                          <h3 className="text-sm sm:text-base font-medium text-amber-900 dark:text-amber-100 mb-1">
                            ✨ Golden Review Alert!
                          </h3>
                          <p className="text-xs sm:text-sm text-amber-800 dark:text-amber-200">
                            You're submitting a special golden review! Your ratings will be highlighted in gold.
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  <div className="space-y-5 sm:space-y-6">
                    {/* Scent Rating */}
                    <div>
                      <Label className="mb-2 sm:mb-3 block">Scent</Label>
                      <div className="flex items-center gap-1.5 sm:gap-2 justify-center sm:justify-start">{[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => { setScentScore(rating); hapticStarTap(); }}
                          className="transition-transform active:scale-95 hover:scale-110 p-1 sm:p-0.5 touch-manipulation"
                          onMouseEnter={() => setScentHover(rating)}
                          onMouseLeave={() => setScentHover(0)}
                        >
                          <Star
                            className={`w-10 h-10 sm:w-9 sm:h-9 transition-colors ${
                              rating <= (scentHover || scentScore)
                                ? isGoldenReview 
                                  ? "fill-current text-amber-500" 
                                  : "fill-current text-primary"
                                : "text-muted-foreground"
                            }`}
                          />
                        </button>
                      ))}
                      </div>
                    </div>

                    {/* Dryness Rating */}
                    <div>
                      <Label className="mb-2 sm:mb-3 block">Dryness</Label>
                      <div className="flex items-center gap-1.5 sm:gap-2 justify-center sm:justify-start">{[1, 2, 3, 4, 5].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => { setDrynessScore(rating); hapticStarTap(); }}
                          className="transition-transform active:scale-95 hover:scale-110 p-1 sm:p-0.5 touch-manipulation"
                          onMouseEnter={() => setDrynessHover(rating)}
                          onMouseLeave={() => setDrynessHover(0)}
                        >
                          <Star
                            className={`w-10 h-10 sm:w-9 sm:h-9 transition-colors ${
                              rating <= (drynessHover || drynessScore)
                                ? isGoldenReview 
                                  ? "fill-current text-amber-500" 
                                  : "fill-current text-primary"
                                : "text-muted-foreground"
                            }`}
                          />
                        </button>
                      ))}
                      </div>
                    </div>

                    {/* Taste Rating */}
                    <div>
                      <Label className="mb-2 sm:mb-3 block">Taste</Label>
                      <div className="flex items-center gap-1.5 sm:gap-2 justify-center sm:justify-start">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <button
                            key={rating}
                            type="button"
                            onClick={() => { setTasteScore(rating); hapticStarTap(); }}
                            className="transition-transform active:scale-95 hover:scale-110 p-1 sm:p-0.5 touch-manipulation"
                            onMouseEnter={() => setTasteHover(rating)}
                            onMouseLeave={() => setTasteHover(0)}
                          >
                            <Star
                              className={`w-10 h-10 sm:w-9 sm:h-9 transition-colors ${
                                rating <= (tasteHover || tasteScore)
                                  ? isGoldenReview 
                                    ? "fill-current text-amber-500" 
                                    : "fill-current text-primary"
                                  : "text-muted-foreground"
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Appearance Rating */}
                    <div>
                      <Label className="mb-2 sm:mb-3 block">Appearance</Label>
                      <div className="flex items-center gap-1.5 sm:gap-2 justify-center sm:justify-start">
                        {[1, 2, 3, 4, 5].map((rating) => (
                          <button
                            key={rating}
                            type="button"
                            onClick={() => { setAppearanceScore(rating); hapticStarTap(); }}
                            className="transition-transform active:scale-95 hover:scale-110 p-1 sm:p-0.5 touch-manipulation"
                            onMouseEnter={() => setAppearanceHover(rating)}
                            onMouseLeave={() => setAppearanceHover(0)}
                          >
                            <Star
                              className={`w-10 h-10 sm:w-9 sm:h-9 transition-colors ${
                                rating <= (appearanceHover || appearanceScore)
                                  ? isGoldenReview 
                                    ? "fill-current text-amber-500" 
                                    : "fill-current text-primary"
                                  : "text-muted-foreground"
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Packaging Toggle (matching Best Used design) */}
                    <div>
                      <Label className="mb-2 sm:mb-3 block">Packaging</Label>
                      <div className="flex items-center gap-2 p-3 sm:p-4 rounded-xl bg-muted/30 border border-border">
                        <button
                          type="button"
                          onClick={() => { setPackagingGood(false); hapticToggleTap(); }}
                          className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-3.5 sm:py-3 rounded-lg transition-all touch-manipulation active:scale-95 ${
                            packagingGood === false
                              ? isGoldenReview 
                                ? 'bg-amber-500 text-white' 
                                : 'bg-primary text-primary-foreground'
                              : packagingHover === false && isGoldenReview
                                ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                                : packagingHover === false
                                  ? 'bg-primary/20 text-primary'
                                  : 'bg-background text-muted-foreground hover:bg-muted'
                          }`}
                          onMouseEnter={() => setPackagingHover(false)}
                          onMouseLeave={() => setPackagingHover(null)}
                        >
                          <Package className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="text-sm sm:text-base">Bad</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => { setPackagingGood(true); hapticToggleTap(); }}
                          className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-3.5 sm:py-3 rounded-lg transition-all touch-manipulation active:scale-95 ${
                            packagingGood === true
                              ? isGoldenReview 
                                ? 'bg-amber-500 text-white' 
                                : 'bg-primary text-primary-foreground'
                              : packagingHover === true && isGoldenReview
                                ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                                : packagingHover === true
                                  ? 'bg-primary/20 text-primary'
                                  : 'bg-background text-muted-foreground hover:bg-muted'
                          }`}
                          onMouseEnter={() => setPackagingHover(true)}
                          onMouseLeave={() => setPackagingHover(null)}
                        >
                          <Package className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="text-sm sm:text-base">Good</span>
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        How would you rate the product packaging?
                      </p>
                    </div>

                    {/* Best Used Toggle */}
                    <div>
                      <Label className="mb-2 sm:mb-3 block">Best Used</Label>
                      <div className="flex items-center gap-2 p-3 sm:p-4 rounded-xl bg-muted/30 border border-border">
                        <button
                          type="button"
                          onClick={() => { setBestUsed('day'); hapticToggleTap(); }}
                          className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-3.5 sm:py-3 rounded-lg transition-all touch-manipulation active:scale-95 ${
                            bestUsed === 'day' || bestUsed === 'both'
                              ? isGoldenReview 
                                ? 'bg-amber-500 text-white' 
                                : 'bg-primary text-primary-foreground'
                              : bestUsedHover === 'day' && isGoldenReview
                                ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                                : bestUsedHover === 'day'
                                  ? 'bg-primary/20 text-primary'
                                  : 'bg-background text-muted-foreground hover:bg-muted'
                          }`}
                          onMouseEnter={() => setBestUsedHover('day')}
                          onMouseLeave={() => setBestUsedHover(null)}
                        >
                          <Sun className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="text-sm sm:text-base">Day</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => { setBestUsed('both'); hapticToggleTap(); }}
                          className={`px-3 sm:px-4 py-3.5 sm:py-3 rounded-lg transition-all touch-manipulation active:scale-95 text-sm sm:text-base ${
                            bestUsed === 'both'
                              ? isGoldenReview 
                                ? 'bg-amber-500 text-white' 
                                : 'bg-primary text-primary-foreground'
                              : bestUsedHover === 'both' && isGoldenReview
                                ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                                : bestUsedHover === 'both'
                                  ? 'bg-primary/20 text-primary'
                                  : 'bg-background text-muted-foreground hover:bg-muted'
                          }`}
                          onMouseEnter={() => setBestUsedHover('both')}
                          onMouseLeave={() => setBestUsedHover(null)}
                        >
                          Both
                        </button>
                        <button
                          type="button"
                          onClick={() => { setBestUsed('night'); hapticToggleTap(); }}
                          className={`flex-1 flex items-center justify-center gap-1.5 sm:gap-2 py-3.5 sm:py-3 rounded-lg transition-all touch-manipulation active:scale-95 ${
                            bestUsed === 'night' || bestUsed === 'both'
                              ? isGoldenReview 
                                ? 'bg-amber-500 text-white' 
                                : 'bg-primary text-primary-foreground'
                              : bestUsedHover === 'night' && isGoldenReview
                                ? 'bg-amber-500/20 text-amber-700 dark:text-amber-300'
                                : bestUsedHover === 'night'
                                  ? 'bg-primary/20 text-primary'
                                  : 'bg-background text-muted-foreground hover:bg-muted'
                          }`}
                          onMouseEnter={() => setBestUsedHover('night')}
                          onMouseLeave={() => setBestUsedHover(null)}
                        >
                          <Moon className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="text-sm sm:text-base">Night</span>
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        When do you find this product works best?
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-between gap-3 mt-6 sm:mt-8">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setStep(1);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="h-11 sm:h-10 order-2 sm:order-1"
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        if (!isStep2Complete()) {
                          toast.error("Please complete all ratings before continuing");
                          return;
                        }
                        hapticStepTransition();
                        setStep(3);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`${
                        isGoldenReview 
                          ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                          : 'gradient-green hover:opacity-90'
                      } transition-opacity px-6 sm:px-8 h-11 sm:h-10 order-1 sm:order-2`}
                      disabled={!isStep2Complete()}
                    >
                      Next: Add Details
                    </Button>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Step 3: Write Review (Free Text + Photos) */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="p-4 sm:p-6 md:p-8 glass-card">
                  <h2 className="text-xl sm:text-2xl mb-4 sm:mb-6">Add Review Details</h2>

                  <div className="space-y-4 sm:space-y-6">
                    <div>
                      <Label htmlFor="title" className="text-sm sm:text-base">Review Title</Label>
                      <Input
                        id="title"
                        placeholder="e.g., Great for evening use"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="mt-2 h-11 sm:h-12 text-base"
                        maxLength={40}
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        {title.length}/40 characters • Summarise your experience in a few words
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="reviewText" className="text-sm sm:text-base">Main Review Details</Label>
                      <Textarea
                        id="reviewText"
                        placeholder="Share your experience with this product..."
                        value={reviewText}
                        onChange={(e) => setReviewText(e.target.value)}
                        className="mt-2 min-h-[150px] text-base"
                        maxLength={1000}
                      />
                      <p className="text-xs text-muted-foreground mt-2">
                        {reviewText.length}/1000 characters • Describe how it worked for you and any notable effects
                      </p>
                    </div>

                    {/* Multiple Photo Upload (up to 2) */}
                    <div>
                      <Label className="mb-2 sm:mb-3 block text-sm sm:text-base">Add Photos (Optional - up to 2)</Label>
                      
                      {/* Photo Previews */}
                      {photoPreviews.length > 0 && (
                        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                          {photoPreviews.map((preview, index) => (
                            <div key={index} className="relative">
                              <img
                                src={preview}
                                alt={`Preview ${index + 1}`}
                                className="w-full h-48 object-cover rounded-xl border border-border"
                              />
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                className="absolute top-2 right-2"
                                onClick={() => handlePhotoRemove(index)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Upload Button */}
                      {photoFiles.length < 2 && (
                        <label className="flex flex-col items-center justify-center w-full h-44 sm:h-40 border-2 border-dashed border-border rounded-xl cursor-pointer hover:border-primary/50 hover:bg-accent/30 transition-all active:scale-[0.98]">
                          <div className="flex flex-col items-center justify-center pt-5 pb-6 px-4">
                            <ImageIcon className="w-9 h-9 sm:w-10 sm:h-10 text-muted-foreground mb-2" />
                            <p className="text-sm text-muted-foreground mb-1 text-center">
                              {photoFiles.length === 0 ? "Click to upload photos" : `Add another photo (${2 - photoFiles.length} remaining)`}
                            </p>
                            <p className="text-xs text-muted-foreground text-center">JPEG, PNG, or WebP (max 5MB each)</p>
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            multiple
                            onChange={handlePhotoSelect}
                          />
                        </label>
                      )}
                      
                      {/* Upload All Button */}
                      {photoFiles.length > 0 && photoUrls.length === 0 && (
                        <div className="mt-3">
                          <Button
                            type="button"
                            onClick={handlePhotosUpload}
                            disabled={uploadingPhotos}
                            className="w-full gradient-green hover:opacity-90 transition-opacity h-11 sm:h-10"
                          >
                            {uploadingPhotos ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                <span className="text-sm sm:text-base">Uploading & Stripping EXIF...</span>
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                <span className="text-sm sm:text-base">Upload {photoFiles.length} Photo{photoFiles.length > 1 ? 's' : ''} (EXIF will be removed)</span>
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                      
                      {photoUrls.length > 0 && (
                        <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                            <p className="text-sm text-green-700 dark:text-green-300">
                              {photoUrls.length} photo{photoUrls.length > 1 ? 's' : ''} uploaded with privacy protection (EXIF removed)
                            </p>
                          </div>
                        </div>
                      )}
                      
                      <p className="text-xs text-muted-foreground mt-2">
                        🔒 All location and metadata (EXIF) will be automatically removed for your privacy. Photos must not contain information that may identify you.
                      </p>
                    </div>

                    {/* Guidelines */}
                    <div className="p-3 sm:p-4 rounded-xl bg-accent/30 border border-primary/20">
                      <div className="flex items-start gap-2 sm:gap-3">
                        <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-xs sm:text-sm mb-2">Review Guidelines</h4>
                          <ul className="text-xs text-muted-foreground space-y-1">
                            <li>• Please do not include dosage advice or medical claims</li>
                            <li>• Stay anonymous, fair and kind</li>
                            <li>• Indecent language is not tolerated</li>
                            <li>• You confirm all comments are your own personal view</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-between gap-3 mt-6 sm:mt-8">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setStep(2);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className="h-11 sm:h-10 order-2 sm:order-1"
                    >
                      Back
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        hapticStepTransition();
                        setStep(4);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      className={`${
                        isGoldenReview 
                          ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                          : 'gradient-green hover:opacity-90'
                      } transition-opacity px-6 sm:px-8 h-11 sm:h-10 order-1 sm:order-2`}
                    >
                      Next: Final Rating
                    </Button>
                  </div>
                </Card>
              </motion.div>
            )}

            {/* Step 4: Overall Effectiveness, Auto Grade & Repurchase */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Card className="p-4 sm:p-6 md:p-8 glass-card">
                  <h2 className="text-xl sm:text-2xl mb-2">Experience & Value</h2>
                  <p className="text-sm text-muted-foreground mb-4 sm:mb-6">
                    Answer these last few questions and we'll calculate your final grade!
                  </p>

                  <div className="space-y-6 sm:space-y-8">
                    {/* Overall Effectiveness Slider with integrated percentage */}
                    <div>
                      <Label className="text-base sm:text-lg mb-3 sm:mb-4 block">Overall Effectiveness</Label>
                      <p className="text-sm text-muted-foreground mb-3 sm:mb-4">
                        How effective was this product overall?
                      </p>
                      
                      {/* Slider with percentage display */}
                      <div className="relative">
                        <div className={isGoldenReview ? "[&_[role=slider]]:bg-amber-500 [&_[role=slider]]:border-amber-500" : ""}>
                          <Slider
                            value={[effectiveness]}
                            onValueChange={(value) => {
                              const v = value[0];
                              if (v !== prevEffectivenessRef.current) {
                                hapticNotch();
                                prevEffectivenessRef.current = v;
                              }
                              setEffectiveness(v);
                            }}
                            min={1}
                            max={100}
                            step={1}
                            className={`w-full ${isGoldenReview ? "[&>span]:bg-amber-500" : ""}`}
                          />
                        </div>
                        
                        {/* Percentage indicator above slider */}
                        <div 
                          className="absolute -top-12 left-0 transition-all duration-200"
                          style={{ 
                            left: `calc(${((effectiveness - 1) / 99) * 100}% - 20px)` 
                          }}
                        >
                          <div className="relative">
                            <div className={`px-3 py-1.5 rounded-lg shadow-lg ${
                              isGoldenReview 
                                ? 'bg-amber-500 text-white' 
                                : 'bg-primary text-primary-foreground'
                            }`}>
                              <span className="font-medium">{effectiveness}%</span>
                            </div>
                            {/* Arrow pointing down */}
                            <div className="absolute left-1/2 -translate-x-1/2 -bottom-1">
                              <div className={`w-2 h-2 rotate-45 ${
                                isGoldenReview ? 'bg-amber-500' : 'bg-primary'
                              }`}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-between text-xs text-muted-foreground mt-6">
                        <span>Not Effective</span>
                        <span>Extremely Effective</span>
                      </div>
                    </div>

                    {/* Value for Money Slider */}
                    <div>
                      <Label className="text-base sm:text-lg mb-3 sm:mb-4 block">Value for Money</Label>
                      <p className="text-sm text-muted-foreground mb-3 sm:mb-4">
                        Was this product value for money?
                      </p>
                      
                      {/* Slider with number display */}
                      <div className="relative">
                        <div className={isGoldenReview ? "[&_[role=slider]]:bg-amber-500 [&_[role=slider]]:border-amber-500" : ""}>
                          <Slider
                            value={[valueForMoney]}
                            onValueChange={(value) => {
                              const v = value[0];
                              if (v !== prevValueForMoneyRef.current) {
                                hapticNotch();
                                prevValueForMoneyRef.current = v;
                              }
                              setValueForMoney(v);
                            }}
                            min={1}
                            max={10}
                            step={1}
                            className={`w-full ${isGoldenReview ? "[&>span]:bg-amber-500" : ""}`}
                          />
                        </div>
                        
                        {/* Number indicator above slider */}
                        <div 
                          className="absolute -top-12 left-0 transition-all duration-200"
                          style={{ 
                            left: `calc(${((valueForMoney - 1) / 9) * 100}% - 20px)` 
                          }}
                        >
                          <div className="relative">
                            <div className={`px-3 py-1.5 rounded-lg shadow-lg ${
                              isGoldenReview 
                                ? 'bg-amber-500 text-white' 
                                : 'bg-primary text-primary-foreground'
                            }`}>
                              <span className="font-medium">{valueForMoney}</span>
                            </div>
                            {/* Arrow pointing down */}
                            <div className="absolute left-1/2 -translate-x-1/2 -bottom-1">
                              <div className={`w-2 h-2 rotate-45 ${
                                isGoldenReview ? 'bg-amber-500' : 'bg-primary'
                              }`}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-between text-xs text-muted-foreground mt-6">
                        <span>Poor Value</span>
                        <span>Great Value</span>
                      </div>
                    </div>

                    {/* Would You Buy Again */}
                    <div>
                      <Label className="text-base sm:text-lg mb-3 sm:mb-4 block text-center">Would you buy it again?</Label>
                      <div className="flex items-center justify-center gap-3 sm:gap-4">
                        <button
                          type="button"
                          onClick={() => { setRepeatPurchase(false); hapticToggleTap(); }}
                          className={`group relative overflow-hidden w-20 h-20 sm:w-24 sm:h-24 rounded-2xl transition-all duration-300 touch-manipulation active:scale-95 ${
                            !repeatPurchase
                              ? 'bg-destructive text-destructive-foreground shadow-xl scale-[1.05]'
                              : 'bg-card border-2 border-border text-muted-foreground hover:border-destructive/30 hover:bg-destructive/5'
                          }`}
                        >
                          <div className="relative z-10 flex flex-col items-center justify-center h-full gap-1">
                            <svg 
                              className={`transition-all ${
                                !repeatPurchase ? 'w-7 h-7 sm:w-8 sm:h-8' : 'w-6 h-6 sm:w-7 sm:h-7'
                              }`}
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2.5} 
                                d="M6 18L18 6M6 6l12 12" 
                              />
                            </svg>
                            <span className="text-xs sm:text-sm">No</span>
                          </div>
                          {!repeatPurchase && (
                            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
                          )}
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => { setRepeatPurchase(true); hapticToggleTap(); }}
                          className={`group relative overflow-hidden w-20 h-20 sm:w-24 sm:h-24 rounded-2xl transition-all duration-300 touch-manipulation active:scale-95 ${
                            repeatPurchase
                              ? 'gradient-green text-primary-foreground shadow-xl scale-[1.05]'
                              : 'bg-card border-2 border-border text-muted-foreground hover:border-primary/30 hover:bg-primary/5'
                          }`}
                        >
                          <div className="relative z-10 flex flex-col items-center justify-center h-full gap-1">
                            <svg 
                              className={`transition-all ${
                                repeatPurchase ? 'w-7 h-7 sm:w-8 sm:h-8' : 'w-6 h-6 sm:w-7 sm:h-7'
                              }`}
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2.5} 
                                d="M5 13l4 4L19 7" 
                              />
                            </svg>
                            <span className="text-xs sm:text-sm">Yes</span>
                          </div>
                          {repeatPurchase && (
                            <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground text-center mt-3">
                        Your response helps other patients make informed decisions
                      </p>
                    </div>

                    {/* Auto-Generated Grade (READ ONLY) */}
                    <div className="relative p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-background border-2 border-primary/30 text-center overflow-hidden">
                      {/* Decorative background elements */}
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(45,122,79,0.1),transparent_50%)]" />
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(45,122,79,0.05),transparent_50%)]" />
                      
                      <div className="relative z-10">
                        <p className="text-xs uppercase tracking-wider text-primary/70 mb-4">Final Grade</p>
                        
                        {/* Grade Display */}
                        <div className="flex items-center justify-center gap-6 mb-5">
                          <div className={`${getGradientClass(currentGrade)} w-32 h-32 sm:w-40 sm:h-40 rounded-3xl mx-auto flex items-center justify-center shadow-2xl relative overflow-hidden transform transition-all duration-300 hover:scale-105`}>
                            {/* Shine effect */}
                            <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent" />
                            <span className="text-6xl sm:text-7xl text-white relative z-10">{currentGrade}</span>
                          </div>
                        </div>
                        
                        {/* Grade Description */}
                        <div className="space-y-2">
                          <p className="text-base sm:text-lg text-foreground">
                            {currentGrade === 'A+' && 'Exceptional Product'}
                            {currentGrade === 'A' && 'Outstanding Quality'}
                            {currentGrade === 'A-' && 'Excellent Choice'}
                            {currentGrade === 'B+' && 'Very Good Product'}
                            {currentGrade === 'B' && 'Good Quality'}
                            {currentGrade === 'B-' && 'Above Average'}
                            {currentGrade === 'C+' && 'Satisfactory'}
                            {currentGrade === 'C' && 'Acceptable'}
                            {currentGrade === 'C-' && 'Fair'}
                            {currentGrade === 'D' && 'Below Expectations'}
                            {currentGrade === 'F' && 'Unsatisfactory'}
                          </p>
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            Calculated from all your ratings
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* 48hr Approval Notice */}
                    <div className="p-3 sm:p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                      <div className="flex items-start gap-2 sm:gap-3">
                        <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                        <p className="text-xs sm:text-sm text-amber-900 dark:text-amber-100">
                          For everyone's safety, reviews can take up to 48 hours for approval.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-between gap-3 mt-6 sm:mt-8">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setStep(3);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      disabled={isSubmitting}
                      className="h-11 sm:h-10 order-2 sm:order-1"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      className={`h-11 sm:h-10 order-1 sm:order-2 ${
                        isGoldenReview 
                          ? 'bg-amber-500 hover:bg-amber-600 text-white' 
                          : 'gradient-green hover:opacity-90'
                      } transition-opacity px-6 sm:px-8`}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          <span className="text-sm sm:text-base">Submitting...</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          <span className="text-sm sm:text-base">Submit Review</span>
                        </>
                      )}
                    </Button>
                  </div>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>
        </form>
      </div>
    </div>
  );
}